import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, getSupabase, isSupabaseConfigured } from "../lib/supabase";

export interface ArcherStatus {
  last_mustered: string | null;
  authorities_changed_since: number;
}

export function useArcherStatus(caseId: string | undefined, poll: boolean) {
  return useQuery<ArcherStatus, Error>({
    queryKey: ["archer-status", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    refetchInterval: poll ? 4000 : false,
    staleTime: poll ? 0 : 30 * 1000,
    queryFn: async () => {
      if (!caseId) return { last_mustered: null, authorities_changed_since: 0 };
      const { data, error } = await berserkr().rpc("fetch_archer_status", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as ArcherStatus) ?? { last_mustered: null, authorities_changed_since: 0 };
    },
  });
}

/**
 * Muster the Archers — re-assess every cause of action against the available
 * defenses (authorities + evidence). Background job (202); poll until a new run
 * lands, then refresh the claims.
 */
export function useMusterArchers(caseId: string | undefined) {
  const qc = useQueryClient();
  const [mustering, setMustering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef<string | null>(null);

  const status = useArcherStatus(caseId, mustering);
  const last = status.data?.last_mustered ?? null;

  useEffect(() => {
    if (!mustering) return;
    if (last && last !== startedAt.current) {
      setMustering(false);
      qc.invalidateQueries({ queryKey: ["case-claims", caseId] });
      qc.invalidateQueries({ queryKey: ["archer-status", caseId] });
    }
  }, [mustering, last, caseId, qc]);

  useEffect(() => {
    if (!mustering) return;
    const t = setTimeout(() => {
      setMustering(false);
      setError("muster is taking longer than expected — check back shortly");
    }, 180 * 1000);
    return () => clearTimeout(t);
  }, [mustering]);

  const muster = useCallback(async () => {
    if (!caseId || mustering) return;
    const sb = getSupabase();
    if (!sb) { setError("Supabase not configured"); return; }
    setError(null);
    startedAt.current = last;
    setMustering(true);
    const { error: invokeErr } = await sb.functions.invoke("muster-the-archers", { body: { case_id: caseId } });
    if (invokeErr) { setError(invokeErr.message); setMustering(false); }
  }, [caseId, mustering, last]);

  return { muster, mustering, error, status: status.data };
}
